// A scan that has not ended (queued or running): its state, the address, the steps with the one it is in,
// one indicator of the pages really analysed out of the pages planned (never a percentage or a time that
// is not known) and, once at least one page has finished, a rough time left from measured pace (never a
// percentage as the main indicator). Screen readers get the state changes from the screen's status line, not
// from the moving parts.
import { CheckIcon } from '@radix-ui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureUrl, type LivePage, type LiveScan, type Scan } from '../api';
import { estimateRemainingMs, etaDisplay, type ProgressSample, pushProgressSample } from '../scan-eta';
import { LIVE_PHASES, livePhase } from '../scan-logic';
import { pathOf } from '../util';
import { ToneIcon, worstTone } from '../viz';
import { OutcomeIcon, RefreshError, type RefreshState, ScanAddress } from './scan-status';

/** Queued pages drawn as empty tiles; beyond this many, one tile says how many more. */
const QUEUED_TILES = 8;

function useScanPace(done: number, running: boolean, total: number) {
  const [samples, setSamples] = useState<ProgressSample[]>([]);
  const prevDone = useRef<number | null>(null);
  useEffect(() => {
    if (!running || total <= 0) {
      prevDone.current = null;
      setSamples([]);
      return;
    }
    const at = Date.now();
    if (prevDone.current === null) {
      prevDone.current = done;
      setSamples([{ done, at }]);
      return;
    }
    if (done !== prevDone.current) {
      prevDone.current = done;
      setSamples((current) => pushProgressSample(current, done, at));
    }
  }, [running, done, total]);
  return samples;
}

/**
 * Real page counts while the scan runs; the bar reflects the same proportion. A rough ETA appears only from
 * measured pace once at least one page has finished.
 */
function PageProgress({ done, total, running }: { done: number; total: number; running: boolean }) {
  const { t } = useTranslation();
  const samples = useScanPace(done, running, total);
  const remaining = running ? estimateRemainingMs(samples, done, total) : null;
  const eta = remaining === null ? null : etaDisplay(remaining);
  return (
    <div className="live-progress">
      <p className="live-count">{t('status.progress', { done, total })}</p>
      {eta && (
        <p className="live-eta">
          {eta.unit === 'seconds'
            ? t('status.progressEta.seconds', { count: eta.count })
            : t('status.progressEta.minutes', { count: eta.count })}
        </p>
      )}
      <span className="live-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, (done / total) * 100)}%` }} />
      </span>
    </div>
  );
}

function DoneTile({ scanId, page }: { scanId: string; page: LivePage }) {
  const { t } = useTranslation();
  // The numbers of the page are told once the analysis ends, in the units of its summary.
  const tone = page.blocked || page.error ? 'none' : worstTone(page.counts);
  return (
    <li className="live-tile" data-tone={tone}>
      <div className="tile-shot">
        {page.thumbnail ? (
          <img src={captureUrl(scanId, page.key, 'miniatura')} alt="" decoding="async" />
        ) : (
          <span className="tile-shot-empty" aria-hidden="true" />
        )}
      </div>
      <div className="tile-caption">
        <span className="tile-title">{page.title || pathOf(page.url)}</span>
        <span className="tile-path">{pathOf(page.url)}</span>
        {page.blocked ? (
          <span className="tone-chip" data-tone="none">
            {t('live.blocked')}
          </span>
        ) : page.error ? (
          <span className="tone-chip" data-tone="none">
            {t('live.error')}
          </span>
        ) : (
          <span className="tone-chip" data-tone="ok">
            <ToneIcon tone="ok" />
            {t('live.analysed')}
          </span>
        )}
      </div>
    </li>
  );
}

/** The phases the engine goes through, with the one it is in marked in words and not only by colour. */
function Phases({ phase }: { phase: (typeof LIVE_PHASES)[number] }) {
  const { t } = useTranslation();
  const current = LIVE_PHASES.indexOf(phase);
  return (
    <ol className="live-phases" aria-label={t('status.phases.title')}>
      {LIVE_PHASES.map((name, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'next';
        return (
          <li key={name} data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="phase-mark" aria-hidden="true">
              {state === 'done' ? <CheckIcon className="phase-check" /> : null}
            </span>
            {t(`status.phases.${name}`)}
            {state === 'done' && <span className="sr-only"> ({t('status.phases.done')})</span>}
            {state === 'current' && <span className="sr-only"> ({t('status.phases.current')})</span>}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The live view of a scan that has not ended; the result or the failure replaces it. It only reads:
 * `refresh` repeats the reading of the state and never starts an analysis.
 */
export function LiveScanView({
  scan,
  live,
  url,
  refresh,
  headingLevel = 3,
}: {
  scan: Scan;
  /** What the engine has reported while it runs; null before it does. */
  live: LiveScan | null;
  url: string;
  refresh: RefreshState;
  headingLevel?: 2 | 3;
}) {
  const { t } = useTranslation();
  const running = scan.status === 'running';

  const pages = live?.pages ?? [];
  const current = live?.current ?? [];
  const done = live?.done ?? scan.progress.done;
  const total = Math.max(live?.total ?? scan.progress.total ?? 0, done);
  const waiting = Math.max(0, total - pages.length - current.length);
  const phase = livePhase({
    status: running ? 'running' : 'queued',
    done,
    total,
    current: current.length,
  });

  const Heading = `h${headingLevel}` as const;
  return (
    <section
      className="live-scan"
      aria-labelledby="live-title"
      data-phase={phase}
      data-outcome={scan.outcome}
    >
      <div className="live-status">
        <Heading id="live-title" tabIndex={-1} className="focus:outline-none">
          <OutcomeIcon outcome={scan.outcome} />
          {t(`status.outcomes.${scan.outcome}`)}
        </Heading>
        <ScanAddress url={url} />
        <Phases phase={phase} />
        {running ? (
          total > 0 && <PageProgress done={done} total={total} running={running} />
        ) : (
          <p>{t('status.queued.text')}</p>
        )}
        <p className="live-leave">{t('status.leave')}</p>
        {refresh.failed && <RefreshError refresh={refresh} dark />}
      </div>
      {(pages.length > 0 || current.length > 0 || waiting > 0) && (
        <ul className="live-mosaic" aria-label={t('live.mosaic')}>
          {pages.map((page) => (
            <DoneTile key={page.key} scanId={scan.id} page={page} />
          ))}
          {current.map((url) => (
            <li key={`current-${url}`} className="live-tile tile-scanning">
              <div className="tile-shot">
                <span className="tile-skeleton" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="tile-beam" aria-hidden="true" />
              </div>
              <div className="tile-caption">
                <span className="tile-title">{pathOf(url)}</span>
                <span className="tile-path">{t('live.analysing')}</span>
              </div>
            </li>
          ))}
          {Array.from({ length: Math.min(waiting, QUEUED_TILES) }, (_, i) => (
            // Empty tiles: nothing to tell them apart but their position.
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholders without identity.
            <li key={`waiting-${i}`} className="live-tile tile-waiting">
              <div className="tile-shot" />
              <div className="tile-caption">
                <span className="tile-path">
                  {i === QUEUED_TILES - 1 && waiting > QUEUED_TILES
                    ? t('live.moreWaiting', { count: waiting - QUEUED_TILES + 1 })
                    : t('live.waiting')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
